import React, { useEffect, useState } from "react";
import {
  Button,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import moment from "moment";
import { AsyncPaginatedSelect } from "components/common/AsyncPaginatedSelect";
import { getModeOfPaymentOptions } from "features/Meta/services/MetaService";
import { getClientAdvance } from "features/ClientAdvances/services/ClientAdvanceService";
import type { ClientAdvance } from "features/ClientAdvances/types/client-advance.types";
import { formatCurrency } from "utils/currency";
import LivePreviewBadge from "./components/LivePreviewBadge";

interface ReceiveModalProps {
  show?: boolean;
  record?: ClientAdvance | null;
  submitting?: boolean;
  onCloseClick?: () => void;
  onConfirm: (payload: {
    modeOfPaymentId: number;
    paymentDate?: string | null;
    referenceNumber?: string | null;
  }) => void;
}

const ReceiveModal = ({
  show,
  record,
  submitting = false,
  onCloseClick,
  onConfirm,
}: ReceiveModalProps) => {
  const [modeOfPaymentId, setModeOfPaymentId] = useState<number | "">("");
  const [paymentDate, setPaymentDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [liveRecord, setLiveRecord] = useState<ClientAdvance | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);

  // Re-fetch right before showing the confirm dialog — the displayed amount for a DRAFT
  // percentage advance is a live estimate and may have moved since the list/overview was loaded.
  useEffect(() => {
    if (!show || !record?.id) {
      setLiveRecord(null);
      return;
    }
    let cancelled = false;
    setLiveLoading(true);
    getClientAdvance(record.id)
      .then((res) => {
        if (!cancelled && res.success) setLiveRecord(res.data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLiveLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [show, record?.id]);

  const displayRecord = liveRecord ?? record;

  const handleClose = () => {
    setModeOfPaymentId("");
    setPaymentDate("");
    setReferenceNumber("");
    setAttempted(false);
    onCloseClick?.();
  };

  const handleConfirm = () => {
    setAttempted(true);
    if (!modeOfPaymentId) return;
    onConfirm({
      modeOfPaymentId: Number(modeOfPaymentId),
      paymentDate: paymentDate || null,
      referenceNumber: referenceNumber.trim() || null,
    });
  };

  return (
    <Modal fade isOpen={show} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>
        Receive — {record?.code ?? `#${record?.id}`}
      </ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          This records how the money was received and creates the payment.
          The advance will move to <strong>Processed</strong>.
        </p>
        <div className="mb-3 small">
          <span className="text-muted">Amount to be received: </span>
          {liveLoading ? (
            <Spinner size="sm" className="ms-1" />
          ) : (
            <span className="fw-medium">
              {formatCurrency(displayRecord?.amount)}
              {displayRecord?.isLivePreview ? <LivePreviewBadge /> : null}
            </span>
          )}
          {displayRecord?.isLivePreview ? (
            <div className="text-muted mt-1">
              This is a live estimate — the linked job&apos;s agreed amount has
              changed since this advance was created, so the final amount may
              be recalculated at the moment it&apos;s received.
            </div>
          ) : null}
        </div>
        <div className="mb-3">
          <Label className="form-label">
            Mode of payment <span className="text-danger">*</span>
          </Label>
          <AsyncPaginatedSelect
            fetchOptions={getModeOfPaymentOptions}
            placeholder="Search modes…"
            value={modeOfPaymentId}
            onChange={(val: any) => setModeOfPaymentId(val?.value ?? "")}
            isInvalid={attempted && !modeOfPaymentId}
          />
          {attempted && !modeOfPaymentId ? (
            <FormFeedback type="invalid" className="d-block">
              Mode of payment is required.
            </FormFeedback>
          ) : null}
        </div>
        <div className="mb-3">
          <Label className="form-label">
            Payment date <span className="text-muted fw-normal">(optional)</span>
          </Label>
          <Flatpickr
            className="form-control"
            placeholder="Select date"
            options={{ altInput: true, altFormat: "d M, Y", dateFormat: "d M, Y" }}
            onChange={(date: any) =>
              setPaymentDate(date[0] ? moment(date[0]).format("YYYY-MM-DD") : "")
            }
            value={paymentDate ? new Date(paymentDate) : ""}
            disabled={submitting}
          />
        </div>
        <div className="mb-0">
          <Label className="form-label">
            Reference number <span className="text-muted fw-normal">(optional)</span>
          </Label>
          <Input
            type="text"
            placeholder="UTR, cheque no., transaction id…"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            disabled={submitting}
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="light" disabled={submitting} onClick={handleClose}>
          Cancel
        </Button>
        <Button color="success" disabled={submitting} onClick={handleConfirm}>
          {submitting ? "Please wait…" : "Receive"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ReceiveModal;
